import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { storeManageUrl } from "../../lib/billing";
import { PRICES } from "../../lib/stripe-config";
import type { PaymentFix } from "../../hooks/useSubscription";
import DeleteAccountDialog from "../dialogs/settings/DeleteAccountDialog";
import LegalLinks from "../misc/LegalLinks";

interface PaywallProps {
  hasUsedTrial: boolean;
  paymentFailed: boolean;
  paymentFix: PaymentFix;
  onCheckout: (priceId: string) => Promise<string | null>;
  onFixPayment: () => Promise<string | null>;
}

interface PaywallOffer {
  body: string;
  button: string;
  error: string;
  terms: string | null;
}

function paywallOffer(
  paymentFailed: boolean,
  hasUsedTrial: boolean,
  paymentFix: PaymentFix,
): PaywallOffer {
  if (paymentFailed && (paymentFix === "play" || paymentFix === "apple")) {
    return {
      body: "The last charge did not go through.",
      button: "Update payment",
      error: "Payment could not open. Try again.",
      terms: null,
    };
  }
  if (paymentFailed) {
    return {
      body: "The last charge did not go through. Update the card and JuDI opens again.",
      button: "Update card",
      error: "The card update could not open. Try again.",
      terms: null,
    };
  }
  if (hasUsedTrial) {
    return {
      body: "The charge starts today. You can cancel later and keep JuDI until the end of the month you paid for.",
      button: "Subscribe",
      error: "Payment could not start. Try again.",
      terms: "This is a one-month auto-renewing subscription at $5 a month.",
    };
  }
  return {
    body: "1 month free, then $5 a month. Your card is saved when you start.",
    button: "Start 1 month free",
    error: "Payment could not start. Try again.",
    terms:
      "This is a one-month auto-renewing subscription at $5 a month. After 1 month, $5 a month unless you cancel.",
  };
}

const Paywall: React.FC<PaywallProps> = ({
  hasUsedTrial,
  paymentFailed,
  paymentFix,
  onCheckout,
  onFixPayment,
}) => {
  const { signOut, isLoading: isSigningOut } = useAuth();
  const [isStarting, setIsStarting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const offer = paywallOffer(paymentFailed, hasUsedTrial, paymentFix);

  const start = async () => {
    const storeUrl = paymentFailed
      ? storeManageUrl(
          paymentFix === "play" ? "google" : paymentFix === "apple" ? "apple" : null,
        )
      : null;
    if (storeUrl) {
      window.location.href = storeUrl;
      return;
    }
    if (!paymentFailed && !PRICES.monthly.id) {
      setError("Payment is not available right now.");
      return;
    }
    setIsStarting(true);
    setError(null);
    try {
      const url = paymentFailed
        ? await onFixPayment()
        : await onCheckout(PRICES.monthly.id);
      if (url) {
        window.location.href = url;
        return;
      }
      setError(offer.error);
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-bg-base flex items-center justify-center p-6">
      <div
        role="dialog"
        aria-label="Subscribe to JuDI"
        className="w-full max-w-md bg-warm-bg-card border border-warm-border rounded-card shadow-soft-md p-8"
      >
        <h1 className="font-heading text-2xl text-warm-text-primary mb-2">
          JuDI is ${PRICES.monthly.amount} a month
        </h1>
        <p className={`text-sm text-warm-text-secondary ${offer.terms ? "mb-2" : "mb-6"}`}>
          {offer.body}
        </p>
        {offer.terms ? (
          <div className="mb-6 space-y-2">
            <p className="text-xs text-warm-text-tertiary">{offer.terms}</p>
            <LegalLinks newTab />
          </div>
        ) : null}
        <button
          type="button"
          onClick={start}
          disabled={isStarting}
          className="w-full py-3 rounded-soft bg-warm-blue hover:bg-warm-blue-dark disabled:opacity-50 text-white font-medium transition-colors"
        >
          {isStarting ? "Opening payment…" : offer.button}
        </button>
        {error ? (
          <p className="mt-3 text-sm text-warm-text-secondary" role="alert">
            {error}
          </p>
        ) : null}
        <p className="mt-6 text-sm text-warm-text-tertiary">
          Your tasks stay saved. You can delete the account if you do not want
          to subscribe.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={signOut}
            disabled={isSigningOut}
            className="px-4 py-2 text-sm font-medium text-warm-text-secondary bg-warm-bg-elevated hover:bg-warm-border-subtle rounded-soft transition-colors disabled:opacity-50"
          >
            {isSigningOut ? "Signing out…" : "Sign out"}
          </button>
          <button
            type="button"
            onClick={() => setShowDelete(true)}
            className="px-4 py-2 text-sm font-medium text-white rounded-soft transition-colors"
            style={{ backgroundColor: "var(--color-recording)" }}
          >
            Delete account
          </button>
        </div>
      </div>
      {showDelete ? (
        <DeleteAccountDialog
          onCancel={() => setShowDelete(false)}
          storeWarning={
            paymentFix === "apple" || paymentFix === "play" ? paymentFix : null
          }
        />
      ) : null}
    </div>
  );
};

export default Paywall;
